// Aplica filtrado visual y visibilidad del arbol de destinos/hoteles en DOM.
const createFilterDomService = (host) => {
  const service = {
    buildHotelScoreById(searchResult) {
      const hotelScoreById = new Map();

      (searchResult?.results || []).forEach(({ item, score }) => {
        if (item?._kind === "hotel" && item._id) {
          const validScore = Number.isFinite(score) ? score : Number.POSITIVE_INFINITY;
          const previousScore = hotelScoreById.get(item._id) ?? Number.POSITIVE_INFINITY;
          if (validScore < previousScore) {
            hotelScoreById.set(item._id, validScore);
          }
        }
      });

      return hotelScoreById;
    },

    getGroupBestScore(hotelIds, hotelScoreById) {
      return hotelIds.reduce((best, id) => Math.min(best, hotelScoreById.get(id) ?? Number.POSITIVE_INFINITY), Number.POSITIVE_INFINITY);
    },

    extractHotelGroups(hotelsRootList) {
      const fixedNodes = [];
      const groups = [];
      const items = Array.from(hotelsRootList.children);
      let index = 0;

      while (index < items.length) {
        const item = items[index];
        const hotelId = item.dataset.hotelId;

        if (!hotelId) {
          fixedNodes.push(item);
          index += 1;
          continue;
        }

        if (item.classList.contains("mod--resortParent")) {
          const nodes = [item];
          const hotelIds = [];
          let nextIndex = index + 1;

          while (nextIndex < items.length && items[nextIndex].classList.contains("mod--resortChild")) {
            nodes.push(items[nextIndex]);
            if (items[nextIndex].dataset.hotelId) {
              hotelIds.push(items[nextIndex].dataset.hotelId);
            }
            nextIndex += 1;
          }

          groups.push({ nodes, hotelIds, originalIndex: groups.length });
          index = nextIndex;
          continue;
        }

        groups.push({ nodes: [item], hotelIds: [hotelId], originalIndex: groups.length });
        index += 1;
      }

      return { fixedNodes, groups };
    },

    reorderHotelsBySearchScore(fastbooking, searchResult) {
      if (!fastbooking) {
        return;
      }

      const defaultModal = fastbooking.querySelector(host.props.modals.destinations.default);
      const hotelsRootList = host.getRootListFromBlock(defaultModal?.querySelector(`.${host.props.modifiers.blocks.secondBlock}`));

      if (!hotelsRootList) {
        return;
      }

      const { fixedNodes, groups } = service.extractHotelGroups(hotelsRootList);
      if (groups.length <= 1) {
        return;
      }

      const hotelScoreById = service.buildHotelScoreById(searchResult);
      const sortedGroups = [...groups].sort((a, b) => {
        const scoreA = service.getGroupBestScore(a.hotelIds, hotelScoreById);
        const scoreB = service.getGroupBestScore(b.hotelIds, hotelScoreById);

        if (scoreA !== scoreB) {
          return scoreA - scoreB;
        }
        return a.originalIndex - b.originalIndex;
      });

      [...fixedNodes, ...sortedGroups.flatMap((g) => g.nodes)].forEach((node) => {
        hotelsRootList.appendChild(node);
      });
    },

    isFilterMatch(query, text) {
      return query.length < 3 || text.includes(query);
    },

    isShortQuery(query) {
      return query !== "" && query.length < 3;
    },

    setDefaultModalVisibility(defaultModal, emptyModal) {
      if (defaultModal) {
        defaultModal.classList.add(host.props.modifiers.show);
      }
      if (emptyModal) {
        emptyModal.classList.remove(host.props.modifiers.show);
      }
    },

    setDefaultHeaders(defaultModal, modalHeaders) {
      if (defaultModal && modalHeaders.length === 2) {
        modalHeaders[0].textContent = defaultModal.dataset.recommendedDestinations;
        modalHeaders[1].textContent = defaultModal.dataset.recommendedHotels;
      }
    },

    setFilteredHeaders(defaultModal, modalHeaders) {
      if (defaultModal && modalHeaders.length === 2) {
        modalHeaders[0].textContent = defaultModal.dataset.destinations;
        modalHeaders[1].textContent = defaultModal.dataset.hotels;
      }
    },

    showAllResultState({ accordion, destinationInputLowercase, defaultModal, modalHeaders, filteringByDest }) {
      service.setDefaultHeaders(defaultModal, modalHeaders);
      host.setIconsOnMobile(defaultModal);

      if (filteringByDest) {
        accordion.querySelector(`.${host.props.modifiers.highlight.grey}`)?.classList.remove(host.props.modifiers.highlight.grey);
      } else {
        accordion.classList.remove(host.props.modifiers.highlight.lightBlue);
      }

      host.printMatchingOccurences(accordion, destinationInputLowercase);
    },

    showFilteredState({ accordion, destinationInputLowercase, defaultModal, modalHeaders, filteringByDest }) {
      service.setFilteredHeaders(defaultModal, modalHeaders);
      host.setIconsOnMobile(defaultModal, true);

      if (filteringByDest) {
        host.toggleActiveElem(accordion);
      }

      host.printMatchingOccurences(accordion, destinationInputLowercase, true);
    },

    showAccordionResult({ accordion, parent, filteringByDest }) {
      if (filteringByDest) {
        if (parent) {
          parent.classList.remove(host.props.modifiers.hide);
        }
        return;
      }
      accordion.classList.remove(host.props.modifiers.hide);
    },

    isLongEnoughQuery(query) {
      return query !== "" && query.length >= 3;
    },

    resetAccordionForLongQuery(accordion, query) {
      accordion.classList.remove(host.props.modifiers.highlight.lightBlue);

      if (accordion.closest(`.${host.props.modifiers.blocks.firstBlock}`)) {
        accordion.querySelector(`.${host.props.modifiers.highlight.grey}`)?.classList.remove(host.props.modifiers.highlight.grey);
        if (accordion.classList.contains(host.props.modifiers.levels.first)) {
          accordion.classList.add(host.props.modifiers.hide);
        }
      } else if (accordion.closest(`.${host.props.modifiers.blocks.secondBlock}`)) {
        accordion.classList.add(host.props.modifiers.hide);
      }

      host.printMatchingOccurences(accordion, query);
    },

    prepareLongQueryState({ query, accordions, defaultModal, modalHeaders, emptyModal, emptyResult }) {
      if (defaultModal) {
        defaultModal.classList.remove(host.props.modifiers.show);
      }
      if (emptyModal) {
        emptyModal.classList.add(host.props.modifiers.show);
      }

      if (defaultModal && modalHeaders.length === 2) {
        modalHeaders[0].textContent = defaultModal.dataset.recommendedDestinations;
        modalHeaders[1].textContent = defaultModal.dataset.recommendedHotels;
        host.setIconsOnMobile(defaultModal);
      }

      if (emptyResult) {
        emptyResult.textContent = `"${query}"`;
      }

      accordions.forEach((accordion) => {
        service.resetAccordionForLongQuery(accordion, query);
      });
    },

    getAccordionComparableText(accordion) {
      const accordionText = accordion.querySelector(host.props.accordions.text);
      if (accordionText) {
        return {
          text: accordionText.textContent.trim().toLowerCase(),
          filteringByDest: true,
        };
      }

      return {
        text: accordion.textContent.trim().toLowerCase(),
        filteringByDest: false,
      };
    },

    applyFilteredResult({ accordion, destinationInputLowercase, text, defaultModal, emptyModal, emptyResult, modalHeaders, filteringByDest = false }) {
      if (!service.isFilterMatch(destinationInputLowercase, text)) {
        return;
      }

      const parent = accordion.closest(`ul.${host.props.modifiers.levels.first} > ${host.props.accordions.label}`);
      service.setDefaultModalVisibility(defaultModal, emptyModal);

      if (destinationInputLowercase === "" || service.isShortQuery(destinationInputLowercase)) {
        service.showAllResultState({ accordion, destinationInputLowercase, defaultModal, modalHeaders, filteringByDest });
        if (emptyResult) {
          emptyResult.textContent = "";
        }

        service.showAccordionResult({ accordion, parent, filteringByDest });
        return;
      }

      service.showFilteredState({ accordion, destinationInputLowercase, defaultModal, modalHeaders, filteringByDest });

      if (emptyResult) {
        emptyResult.textContent = "";
      }

      service.showAccordionResult({ accordion, parent, filteringByDest });
    },

    filterDestinationInput(fastbooking, destinationInput) {
      if (!fastbooking || !destinationInput) {
        return;
      }

      const accordions = Array.from(fastbooking.querySelectorAll(host.props.accordions.label));
      if (!accordions.length) {
        return;
      }

      const query = destinationInput.value.toLowerCase();
      const defaultModal = fastbooking.querySelector(host.props.modals.destinations.default);
      const modalHeaders = Array.from(defaultModal?.querySelectorAll(host.props.modals.destinations.headerText) || []);
      const emptyModal = fastbooking.querySelector(host.props.modals.destinations.empty);
      const emptyResult = emptyModal?.querySelector(host.props.modals.destinations.emptyResult);

      if (service.isLongEnoughQuery(query)) {
        service.prepareLongQueryState({
          query,
          accordions,
          defaultModal,
          modalHeaders,
          emptyModal,
          emptyResult,
        });
      }

      accordions.forEach((accordion) => {
        const { text, filteringByDest } = service.getAccordionComparableText(accordion);

        service.applyFilteredResult({
          accordion,
          destinationInputLowercase: query,
          text,
          defaultModal,
          emptyModal,
          emptyResult,
          modalHeaders,
          filteringByDest,
        });
      });
    },

    applyHotelsVisibility(fastbooking, baseVisibleHotelIds = null) {
      if (!fastbooking) {
        return;
      }

      const hotelsRootList = host.getRootListFromBlock(fastbooking.querySelector(host.props.modals.destinations.default)?.querySelector(`.${host.props.modifiers.blocks.secondBlock}`));
      if (!hotelsRootList) {
        return;
      }

      const selectedHotelIds = host.props.selectedDestinationId ? new Set(host.props.searchIndex?.subtreeHotelIdsByParent?.[host.props.selectedDestinationId] || []) : null;
      const searchHotelIds = baseVisibleHotelIds ? new Set(baseVisibleHotelIds) : null;
      const hotelNodes = Array.from(hotelsRootList.querySelectorAll(`${host.props.accordions.label}[data-hotel-id]`));
      hotelNodes.forEach((node) => {
        if (node.classList.contains("mod--resortParent")) {
          node.classList.add(host.props.modifiers.hide);
          return;
        }

        const hotelId = node.dataset.hotelId;
        const shouldShow = (!searchHotelIds || searchHotelIds.has(hotelId)) && (!selectedHotelIds || selectedHotelIds.has(hotelId));

        node.classList.toggle(host.props.modifiers.hide, !shouldShow);
      });

      hotelNodes.forEach((node, nodeIndex) => {
        if (!node.classList.contains("mod--resortParent")) {
          return;
        }

        let hasVisibleChild = false;
        for (let i = nodeIndex + 1; i < hotelNodes.length; i++) {
          if (!hotelNodes[i].classList.contains("mod--resortChild")) {
            break;
          }
          if (!hotelNodes[i].classList.contains(host.props.modifiers.hide)) {
            hasVisibleChild = true;
            break;
          }
        }

        node.classList.toggle(host.props.modifiers.hide, !hasVisibleChild);
      });
    },

    resetDestinationTreeState(fastbooking) {
      if (!fastbooking) {
        return;
      }

      const resetLabelNode = (labelNode) => {
        if (!labelNode) {
          return;
        }

        labelNode.classList.remove(host.props.modifiers.textStyle.light);
        labelNode.textContent = labelNode.dataset.text || labelNode.textContent || "";
      };

      fastbooking.querySelectorAll("[data-destination-id]").forEach((node) => {
        node.classList.remove(host.props.modifiers.hide, host.props.modifiers.active, host.props.modifiers.highlight.lightBlue);

        const parentList = node.parentElement;
        if (parentList?.tagName === "UL" && !parentList.classList.contains(host.props.modifiers.levels.first)) {
          parentList.classList.remove(host.props.modifiers.show);
        }

        const label = node.querySelector(host.props.accordions.text);
        resetLabelNode(label);

        const title = node.querySelector(host.props.accordions.title);
        if (title) {
          title.classList.remove(host.props.modifiers.highlight.grey);
        }
      });

      fastbooking.querySelectorAll("[data-hotel-id]").forEach((node) => {
        node.classList.remove(host.props.modifiers.highlight.lightBlue);
        resetLabelNode(node.querySelector(host.props.accordions.text) || node);
      });
    },

    getAncestorDestinationIds(destinationId) {
      const ancestorIds = [];
      let currentId = host.props.searchIndex?.parentByDestinationId?.[destinationId] || null;

      while (currentId) {
        ancestorIds.push(currentId);
        currentId = host.props.searchIndex?.parentByDestinationId?.[currentId] || null;
      }

      return ancestorIds;
    },

    showSelectedDestinationTree(fastbooking, destinationId) {
      if (!fastbooking || !destinationId) {
        return;
      }

      const selectedNode = fastbooking.querySelector(`[data-destination-id="${destinationId}"]`);
      if (!selectedNode) {
        return;
      }

      const descendantIds = host.props.searchIndex?.subtreeDestinationIdsByParent?.[destinationId] || [];
      const visibleIds = new Set([destinationId, ...descendantIds, ...service.getAncestorDestinationIds(destinationId)]);

      fastbooking.querySelectorAll("[data-destination-id]").forEach((node) => {
        node.classList.toggle(host.props.modifiers.hide, !visibleIds.has(node.dataset.destinationId));
      });

      service.expandPathToDestinationNode(selectedNode);
      selectedNode.querySelectorAll("ul").forEach((listElement) => {
        listElement.classList.add(host.props.modifiers.show);
      });
    },

    expandPathToDestinationNode(destinationNode) {
      let currentNode = destinationNode;

      while (currentNode) {
        currentNode.classList.add(host.props.modifiers.active);
        const parentList = currentNode.parentElement;

        if (parentList?.tagName === "UL" && !parentList.classList.contains(host.props.modifiers.levels.first)) {
          parentList.classList.add(host.props.modifiers.show);
        }

        currentNode = parentList?.closest("[data-destination-id]") || null;
      }
    },

    clearDestinationSearch(fastbooking, destinationInput) {
      Object.assign(host.props, {
        selectedDestinationId: null,
        filteredTree: host.props.sourceTree,
        visibleDestinationIds: [],
        visibleHotelIds: [],
        lastSearchResult: null,
      });

      host.renderTreeFromSearchData(fastbooking);
      host.manageDestinationPopover(fastbooking);
      service.applyHotelsVisibility(fastbooking);
      host.changeDestinationStructureOnMobile(fastbooking, null, false);
      service.filterDestinationInput(fastbooking, destinationInput);
    },

    applySearchResultToDomByIds(fastbooking, destinationInput, searchResult) {
      const query = destinationInput?.value?.trim();
      if (!query) {
        return false;
      }

      const destinationNodes = Array.from(fastbooking.querySelectorAll("[data-destination-id]"));
      const hotelNodes = Array.from(fastbooking.querySelectorAll("[data-hotel-id]"));

      if (!destinationNodes.length && !hotelNodes.length) {
        return false;
      }

      const visibleDestinationIds = new Set(searchResult?.visibleDestinationIds || []);
      const visibleHotelIds = new Set(searchResult?.visibleHotelIds || []);
      const highlightByNodeId = new Map((searchResult?.results || []).map((r) => [r?.item?._id, r?.item?.highlightedLabel]));

      const hasDestOverlap = destinationNodes.some((n) => visibleDestinationIds.has(n.dataset.destinationId));
      const hasHotelOverlap = hotelNodes.some((n) => visibleHotelIds.has(n.dataset.hotelId));

      if (!hasDestOverlap && !hasHotelOverlap) {
        return false;
      }

      fastbooking.querySelectorAll("[data-destination-id]").forEach((node) => {
        node.classList.remove(host.props.modifiers.active);
        const parentList = node.parentElement;
        if (parentList?.tagName === "UL" && !parentList.classList.contains(host.props.modifiers.levels.first)) {
          parentList.classList.remove(host.props.modifiers.show);
        }
      });

      const matchedDestinationIds = new Set();
      (searchResult?.results || []).forEach(({ item }) => {
        if (!item) {
          return;
        }
        if (item._kind === "destination") {
          matchedDestinationIds.add(item._id);
        }
        if (item.parentId) {
          matchedDestinationIds.add(item.parentId);
        }
        (item.ancestors || []).forEach((ancestorId) => {
          matchedDestinationIds.add(ancestorId);
        });
      });

      matchedDestinationIds.forEach((id) => {
        const node = fastbooking.querySelector(`[data-destination-id="${id}"]`);
        if (node) {
          service.expandPathToDestinationNode(node);
        }
      });

      destinationNodes.forEach((node) => {
        const nodeId = node.dataset.destinationId;
        node.classList.toggle(host.props.modifiers.hide, !visibleDestinationIds.has(nodeId));

        const label = node.querySelector(host.props.accordions.text);
        if (label) {
          const highlight = highlightByNodeId.get(nodeId);
          if (highlight) {
            label.innerHTML = highlight;
          } else {
            label.textContent = label.dataset.text || "";
          }
        }
      });

      service.applyHotelsVisibility(fastbooking, visibleHotelIds);
      host.changeDestinationStructureOnMobile(fastbooking, null, true);

      hotelNodes.forEach((node) => {
        const highlight = highlightByNodeId.get(node.dataset.hotelId);
        if (highlight) {
          node.innerHTML = highlight;
        } else {
          node.textContent = node.dataset.text || "";
        }
      });

      service.reorderHotelsBySearchScore(fastbooking, searchResult);

      const defaultModal = fastbooking.querySelector(host.props.modals.destinations.default);
      host.setIconsOnMobile(defaultModal, true);

      return true;
    },
  };

  return service;
};

export default createFilterDomService;
